import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WorkspaceProvider, useWorkspaceContext } from '@/components/workspace/WorkspaceContext';
import { Sidebar } from './tailoring/Sidebar';
import { Main } from './tailoring/Main';
import { Search } from './search-jobs/Search'; // Updated search import
import { Briefcase, FileSignature, ArrowLeft, Compass } from 'lucide-react';
import toast from 'react-hot-toast';
import { handleAppError } from '@/lib/errorUtils';

interface WorkspaceInstanceProps {
  id: string;
  isActive: boolean;
  mode: 'job_search' | 'tailor';
  recordUsage: (cost: number, promptToken: number, completionToken: number) => void;
  fireWebhook: (event: string, payload: any) => void;
  onModeChange?: (mode: 'job_search' | 'tailor' | 'initial') => void;
  onImportJob?: (data: any) => void;
}

const WorkspaceContent: React.FC<{
  onModeChange?: (mode: 'job_search' | 'tailor' | 'initial') => void;
  onImportJob?: (data: any) => void;
  mode: 'job_search' | 'tailor';
  workspaceId: string;
}> = ({ mode, onModeChange, onImportJob, workspaceId }) => {
  const { isActive, jobText, setTargetDetails, setJobText, setJobLink, isWorkspaceLoaded } = useWorkspaceContext();

  const handleSelectJobFromSearch = async (link: string, title?: string, company?: string, description?: string) => {
    const loadingToast = toast.loading('Fetching job description...');
    try {
      let finalJobText = '';
      
      if (description && description.trim().length > 100) {
        // Use the description we already have from JSearch if it's substantial
        finalJobText = description.substring(0, 15000);
      } else {
        const { fetchJobDescriptionFromUrl } = await import('@/services/jobFetcher');
        const { searchJobDescriptionWithAI } = await import('@/services/geminiService');
        
        const res = await fetchJobDescriptionFromUrl(link, searchJobDescriptionWithAI);
        finalJobText = res.text.substring(0, 15000);
      }

      const { INITIAL_TARGET_DETAILS } = await import('@/constants/app');
      
      const newData = {
        jobText: finalJobText || '',
        jobLink: link,
        targetDetails: {
          ...INITIAL_TARGET_DETAILS,
          company: company || '',
          position: title || ''
        }
      };
      
      toast.dismiss(loadingToast);
      if (onImportJob) {
         onImportJob(newData);
      }
      
    } catch (err: any) {
      handleAppError(err, 'Could not fetch job link automatically. You may need to paste the text manually.');
      toast.error('Failed to import job', { id: loadingToast });
    }
  };

  return (
    <div style={{ display: isActive ? 'flex' : 'none' }} className="flex-1 flex flex-col lg:flex-row min-h-0 w-full relative">
      {isWorkspaceLoaded ? (
        <>
          {mode === 'job_search' && (
            <div className="flex-1 w-full h-full flex-col relative min-w-0 flex">
              <Search workspaceId={workspaceId} onClose={() => onModeChange && onModeChange('initial')} onSelectJob={handleSelectJobFromSearch} />
            </div>
          )}

          <AnimatePresence mode="wait">
            {mode === 'tailor' && (
              <motion.div key="tailor" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }} className="flex-1 flex flex-col lg:flex-row w-full h-full relative">
                <Sidebar onBackToOptions={() => onModeChange && onModeChange('initial')} />
                <Main />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : null}
    </div>
  );
};

export const WorkspaceInstance: React.FC<WorkspaceInstanceProps> = ({ id, isActive, mode, recordUsage, fireWebhook, onModeChange, onImportJob }) => {
  return (
    <WorkspaceProvider id={id} isActive={isActive} recordUsage={recordUsage} fireWebhook={fireWebhook}>
      <WorkspaceContent mode={mode} onModeChange={onModeChange} onImportJob={onImportJob} workspaceId={id} />
    </WorkspaceProvider>
  );
};

export { Tabs } from '@/components/workspace/Tabs';
export { ResetModal } from '@/components/workspace/ResetModal';